import { useState, useEffect, useCallback, useRef } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../components/layout/Navbar';
import Footer from '../components/layout/Footer';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Skeleton } from '../components/ui/skeleton';
import SEO, { PAGE_SEO } from '../components/SEO';
import { TrendingUp, Sparkles, Clock, Search, X, Loader2, Lightbulb, RefreshCw, Star } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const ITEMS_PER_PAGE = 9;

// Helper to get SEO-friendly content URL
const getContentUrl = (item) => `/content/${item.slug || item.content_id}`;

// Daily beauty tips that rotate
const DAILY_TIPS = [
  { tip: "Apply sunscreen as the last step of your morning routine, even on cloudy days.", category: "Sun Protection", icon: "☀️" },
  { tip: "Pat, don't rub! Patting your skincare products helps them absorb better.", category: "Application", icon: "✨" },
  { tip: "Your neck and hands age faster - extend your skincare routine to these areas.", category: "Anti-Aging", icon: "🌟" },
  { tip: "Wait 30 seconds between each skincare layer for better absorption.", category: "Routine", icon: "⏱️" },
  { tip: "Drink at least 8 glasses of water daily for naturally hydrated skin.", category: "Hydration", icon: "💧" },
  { tip: "Change your pillowcase weekly to prevent breakouts and skin irritation.", category: "Hygiene", icon: "🛏️" },
  { tip: "Apply retinol only at night - it degrades in sunlight.", category: "Ingredients", icon: "🌙" },
  { tip: "Use lukewarm water to wash your face - hot water strips natural oils.", category: "Cleansing", icon: "🚿" },
  { tip: "Vitamin C serums work best when applied before moisturizer.", category: "Layering", icon: "🍊" },
  { tip: "Don't skip moisturizer if you have oily skin - dehydration triggers more oil.", category: "Moisturizing", icon: "💦" },
  { tip: "Exfoliate 1-2 times per week max to avoid damaging your skin barrier.", category: "Exfoliation", icon: "🧴" },
  { tip: "Apply eye cream with your ring finger - it applies the gentlest pressure.", category: "Eye Care", icon: "👁️" },
  { tip: "Let your hair air dry when possible to reduce heat damage.", category: "Hair Care", icon: "💇" },
  { tip: "Sleep on a silk pillowcase to prevent hair breakage and frizz.", category: "Hair Care", icon: "🛌" },
];

export default function DailyDrop() {
  const [news, setNews] = useState([]);
  const [filteredNews, setFilteredNews] = useState([]);
  const [displayedNews, setDisplayedNews] = useState([]);
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [usedImages, setUsedImages] = useState(new Set());
  const [dailyTips, setDailyTips] = useState([]);
  const [featuredArticle, setFeaturedArticle] = useState(null);
  const loaderRef = useRef(null);

  // Get random daily tips on mount
  useEffect(() => {
    const shuffled = [...DAILY_TIPS].sort(() => 0.5 - Math.random());
    setDailyTips(shuffled.slice(0, 3));
  }, []);

  const refreshTips = () => {
    const shuffled = [...DAILY_TIPS].sort(() => 0.5 - Math.random());
    setDailyTips(shuffled.slice(0, 3));
  };

  useEffect(() => {
    fetchNews();
  }, []);

  // Update displayed items when filtered news changes
  useEffect(() => {
    setDisplayedNews(filteredNews.slice(0, ITEMS_PER_PAGE));
    setHasMore(filteredNews.length > ITEMS_PER_PAGE);
  }, [filteredNews]);

  const fetchNews = async () => {
    setLoading(true);
    try {
      // Fetch news
      const res = await fetch(`${BACKEND_URL}/api/content?content_type=news&limit=100`);
      const data = await res.json();
      
      // Also fetch latest articles for featured content
      const articlesRes = await fetch(`${BACKEND_URL}/api/content?content_type=article&limit=5`);
      const articles = await articlesRes.json();
      if (articles.length > 0) {
        setFeaturedArticle(articles[Math.floor(Math.random() * articles.length)]);
      }
      
      // Track used images
      const images = new Set(data.map(item => item.image_url).filter(Boolean));
      setUsedImages(images);
      
      setNews(data);
      setFilteredNews(data);
      
      // Extract unique categories
      const cats = [...new Set(data.map(item => item.category))];
      setCategories(cats);
    } catch (error) {
      console.error('Fetch error:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadMore = useCallback(() => {
    if (loadingMore || !hasMore) return;
    
    setLoadingMore(true);
    setTimeout(() => {
      const currentLength = displayedNews.length;
      const nextItems = filteredNews.slice(currentLength, currentLength + ITEMS_PER_PAGE);
      setDisplayedNews(prev => [...prev, ...nextItems]);
      setHasMore(currentLength + nextItems.length < filteredNews.length);
      setLoadingMore(false);
    }, 300);
  }, [displayedNews, filteredNews, loadingMore, hasMore]);

  // Intersection Observer for infinite scroll
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMore && !loadingMore) {
          loadMore();
        }
      },
      { threshold: 0.1 }
    );

    if (loaderRef.current) {
      observer.observe(loaderRef.current);
    }

    return () => observer.disconnect();
  }, [loadMore, hasMore, loadingMore]);

  const handleSearch = useCallback(async () => {
    if (!searchQuery.trim()) {
      setFilteredNews(news);
      return;
    }

    setLoading(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/content/search?q=${encodeURIComponent(searchQuery)}&content_type=news`);
      const data = await res.json();
      setFilteredNews(data);
    } catch (error) {
      console.error('Search error:', error);
    } finally {
      setLoading(false);
    }
  }, [searchQuery, news]);

  const handleCategoryFilter = (category) => {
    setSelectedCategory(category);
    if (!category) {
      setFilteredNews(news);
    } else {
      setFilteredNews(news.filter(item => item.category === category));
    }
  };

  const clearFilters = () => {
    setSearchQuery('');
    setSelectedCategory('');
    setFilteredNews(news);
  };

  // Get unique image for each item
  const getItemImage = (item, index) => {
    if (item.image_url) return item.image_url;
    // Fallback images based on index to avoid repetition
    const fallbacks = [
      'https://images.pexels.com/photos/3785147/pexels-photo-3785147.jpeg',
      'https://images.pexels.com/photos/3762879/pexels-photo-3762879.jpeg',
      'https://images.pexels.com/photos/3997379/pexels-photo-3997379.jpeg',
      'https://images.pexels.com/photos/3737586/pexels-photo-3737586.jpeg',
      'https://images.pexels.com/photos/4041392/pexels-photo-4041392.jpeg',
      'https://images.pexels.com/photos/3762466/pexels-photo-3762466.jpeg',
    ];
    return fallbacks[index % fallbacks.length];
  };

  return (
    <div className="min-h-screen flex flex-col bg-background" data-testid="daily-drop-page">
      <SEO {...PAGE_SEO.dailyDrop} url="/daily-drop" />
      <Navbar />
      
      <main className="flex-1 py-8 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="mb-8">
            <div className="inline-flex items-center gap-2 px-4 py-1.5 bg-gold/10 rounded-sm mb-4">
              <TrendingUp className="w-4 h-4 text-gold" />
              <span className="text-xs font-semibold uppercase tracking-wider text-gold">Daily Updates</span>
            </div>
            <h1 className="font-serif text-4xl sm:text-5xl font-semibold mb-4">The Daily Drop</h1>
            <p className="text-lg text-muted-foreground max-w-2xl">
              Your daily dose of skincare and haircare tips, news, and breakthrough discoveries.
            </p>
          </div>

          {/* Daily Tips Section */}
          <div className="mb-10">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <Lightbulb className="w-5 h-5 text-gold" />
                <h2 className="font-serif text-xl font-semibold">Today's Beauty Tips</h2>
              </div>
              <Button variant="ghost" size="sm" onClick={refreshTips} className="text-muted-foreground hover:text-gold">
                <RefreshCw className="w-4 h-4 mr-1" />
                Refresh
              </Button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {dailyTips.map((tip, index) => (
                <Card key={index} className="bg-gradient-to-br from-gold/5 to-transparent border-gold/20 hover:border-gold/40 transition-colors">
                  <CardContent className="p-4">
                    <div className="flex items-start gap-3">
                      <span className="text-2xl">{tip.icon}</span>
                      <div>
                        <span className="text-xs font-medium text-gold uppercase tracking-wider">{tip.category}</span>
                        <p className="text-sm mt-1">{tip.tip}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>

          {/* Featured Article */}
          {featuredArticle && (
            <div className="mb-10">
              <div className="flex items-center gap-2 mb-4">
                <Star className="w-5 h-5 text-gold" />
                <h2 className="font-serif text-xl font-semibold">Featured Read</h2>
              </div>
              <Link to={getContentUrl(featuredArticle)}>
                <Card className="overflow-hidden hover:shadow-lg transition-shadow border-gold/20 group">
                  <div className="flex flex-col md:flex-row">
                    <div className="md:w-1/3 h-48 md:h-auto relative overflow-hidden">
                      <img 
                        src={featuredArticle.image_url || 'https://images.pexels.com/photos/3785147/pexels-photo-3785147.jpeg'} 
                        alt={featuredArticle.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                      />
                    </div>
                    <CardContent className="flex-1 p-6">
                      <span className="text-xs font-medium text-gold uppercase tracking-wider">{featuredArticle.category}</span>
                      <h3 className="font-serif text-2xl font-semibold mt-2 mb-3 group-hover:text-gold transition-colors">{featuredArticle.title}</h3>
                      <p className="text-muted-foreground line-clamp-2 mb-4">{featuredArticle.summary}</p>
                      <div className="flex items-center gap-4 text-sm text-muted-foreground">
                        <span className="flex items-center gap-1">
                          <Clock className="w-4 h-4" />
                          {featuredArticle.read_time || 5} min read
                        </span>
                        <span className="flex items-center gap-1">
                          <Sparkles className="w-4 h-4 text-gold" />
                          +{featuredArticle.points_reward || 10} pts
                        </span>
                      </div>
                    </CardContent>
                  </div>
                </Card>
              </Link>
            </div>
          )}

          {/* Latest News Section */}
          <div className="mb-6">
            <h2 className="font-serif text-xl font-semibold mb-4">Latest News & Trends</h2>
          </div>

          {/* Search & Filters */}
          <div className="mb-8 space-y-4">
            {/* Search Bar */}
            <div className="flex gap-2 max-w-xl">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  type="text"
                  placeholder="Search news..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                  className="pl-10"
                  data-testid="news-search-input"
                />
              </div>
              <Button onClick={handleSearch} className="btn-primary" data-testid="news-search-btn">
                Search
              </Button>
            </div>

            {/* Category Filters */}
            <div className="flex flex-wrap gap-2">
              <Button
                variant={!selectedCategory ? "default" : "outline"}
                size="sm"
                onClick={() => handleCategoryFilter('')}
                className={!selectedCategory ? 'bg-gold text-black hover:bg-gold/90' : ''}
              >
                All
              </Button>
              {categories.map((cat) => (
                <Button
                  key={cat}
                  variant={selectedCategory === cat ? "default" : "outline"}
                  size="sm"
                  onClick={() => handleCategoryFilter(cat)}
                  className={selectedCategory === cat ? 'bg-gold text-black hover:bg-gold/90' : ''}
                  data-testid={`filter-${cat}`}
                >
                  {cat}
                </Button>
              ))}
              {(searchQuery || selectedCategory) && (
                <Button variant="ghost" size="sm" onClick={clearFilters} className="gap-1">
                  <X className="w-3 h-3" /> Clear
                </Button>
              )}
            </div>
          </div>

          {/* Results Count */}
          {(searchQuery || selectedCategory) && (
            <p className="text-sm text-muted-foreground mb-6">
              Showing {filteredNews.length} result{filteredNews.length !== 1 ? 's' : ''}
              {searchQuery && ` for "${searchQuery}"`}
              {selectedCategory && ` in ${selectedCategory}`}
            </p>
          )}

          {/* News Grid */}
          {loading ? (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[...Array(6)].map((_, i) => (
                <Card key={i} className="border-none">
                  <Skeleton className="aspect-video" />
                  <CardContent className="p-6">
                    <Skeleton className="h-4 w-24 mb-4" />
                    <Skeleton className="h-6 w-full mb-2" />
                    <Skeleton className="h-4 w-full" />
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : filteredNews.length === 0 ? (
            <div className="text-center py-16">
              <Search className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
              <p className="text-muted-foreground">No news articles found. Try a different search term.</p>
            </div>
          ) : (
            <>
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                {displayedNews.map((item, index) => (
                  <Link 
                    key={item.content_id} 
                    to={getContentUrl(item)}
                    className={`group ${index === 0 && displayedNews.length > 2 ? 'md:col-span-2 lg:col-span-2' : ''}`}
                    data-testid={`news-item-${item.content_id}`}
                  >
                    <Card className="bg-card border-none shadow-card card-hover h-full overflow-hidden">
                      <div className={`${index === 0 && displayedNews.length > 2 ? 'aspect-[2/1]' : 'aspect-video'} overflow-hidden`}>
                        <img
                          src={getItemImage(item, index)}
                          alt={item.title}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                          loading="lazy"
                        />
                      </div>
                      <CardContent className="p-6">
                        <div className="flex items-center gap-4 mb-3 text-xs text-muted-foreground">
                          <span className="text-gold font-semibold uppercase tracking-wider">{item.category}</span>
                          <span className="flex items-center gap-1">
                            <Clock className="w-3 h-3" />
                            {item.read_time} min
                          </span>
                        </div>
                        <h2 className={`font-serif font-semibold mb-2 group-hover:text-gold transition-colors ${index === 0 && displayedNews.length > 2 ? 'text-2xl' : 'text-lg'}`}>
                          {item.title}
                        </h2>
                        <p className="text-muted-foreground line-clamp-2">{item.summary}</p>
                        <div className="flex items-center gap-2 mt-4">
                          <div className="flex items-center gap-1 px-2 py-1 bg-gold/10 rounded-sm">
                            <Sparkles className="w-3 h-3 text-gold" />
                            <span className="text-xs font-semibold text-gold">+{item.points_reward} pts</span>
                          </div>
                          <span className="text-xs text-muted-foreground">{item.views} views</span>
                        </div>
                      </CardContent>
                    </Card>
                  </Link>
                ))}
              </div>

              {/* Load More Section */}
              <div ref={loaderRef} className="flex justify-center py-8">
                {loadingMore ? (
                  <div className="flex items-center gap-2 text-muted-foreground">
                    <Loader2 className="w-5 h-5 animate-spin" />
                    <span>Loading more...</span>
                  </div>
                ) : hasMore ? (
                  <Button 
                    onClick={loadMore} 
                    variant="outline" 
                    className="px-8"
                    data-testid="load-more-btn"
                  >
                    Load More Articles
                  </Button>
                ) : displayedNews.length > 0 ? (
                  <p className="text-sm text-muted-foreground">You've reached the end</p>
                ) : null}
              </div>
            </>
          )}
        </div>
      </main>

      <Footer />
    </div>
  );
}
